import { useMemo, useState } from "react";
import { Package, Pencil, Plus, Search, Trash2, ArrowDown, ArrowUp, Users } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { formatMXN } from "@/lib/utils";
import { tipoServicioEnum, type TipoServicio, type Plan } from "./schemas";
import { useDeletePlan, useListPlanes, usePlan } from "./api";
import { PlanFormDialog } from "./PlanFormDialog";
// rc140 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

export function PlanesListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const [tipo, setTipo] = useState<TipoServicio | "">("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: editing } = usePlan(editingId);

  const filters = useMemo(() => ({ page, size: 20, search, tipo_servicio: tipo }), [page, search, tipo]);
  const { data, isLoading, isFetching } = useListPlanes(filters);
  const deleteMut = useDeletePlan();

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  async function handleDelete(p: Plan) {
    // rc18: pre-validación frontend (el backend también lo bloquea por seguridad).
    if (p.contratos_count > 0) {
      alert(
        `No se puede eliminar el plan "${p.nombre}".\n\n` +
        `Tiene ${p.contratos_count} contrato(s) activos o suspendidos. ` +
        `Cancela esos contratos o cámbiales el plan antes de eliminar.`
      );
      return;
    }
    if (!confirm(`¿Eliminar el plan "${p.nombre}"?\nNo podrás crear contratos nuevos con él.`)) return;
    try { await deleteMut.mutateAsync(p.id); }
    catch (e) { alert(`Error: ${(e as { message?: string })?.message}`); }
  }

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Planes</h2>
          <p className="text-xs text-muted-foreground">
            {data ? `${data.total} ${data.total === 1 ? "plan" : "planes"}` : "Cargando…"}
          </p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> <span className="max-md:hidden">Nuevo plan</span><span className="md:hidden">Nuevo</span></Button>
      </div>

      <div className="flex flex-wrap items-end gap-2 max-md:[&>select]:flex-1 max-md:[&>select]:w-auto">
        <div className="relative flex-1 min-w-[220px] max-md:basis-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Buscar por nombre…"
            className="pl-8"
          />
        </div>
        <Select
          value={tipo}
          onChange={(e) => { setTipo(e.target.value as TipoServicio | ""); setPage(1); }}
        >
          <option value="">Todos los tipos</option>
          {tipoServicioEnum.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
      </div>

      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>{search || tipo ? "No hay coincidencias." : "Crea el primer plan de velocidad."}</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((p) => (
              <MobileCard
                key={p.id}
                onClick={() => setEditingId(p.id)}
                title={p.nombre}
                subtitle={p.descripcion || undefined}
                right={<Badge variant={p.activo ? "success" : "secondary"}>{p.activo ? "activo" : "inactivo"}</Badge>}
                actions={
                  <>
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(p.id)}><Pencil size={13} /> Editar</Button>
                    <Button variant="ghost" size="sm" className={p.contratos_count > 0 ? "text-muted-foreground" : "text-destructive"} disabled={p.contratos_count > 0} onClick={() => handleDelete(p)}><Trash2 size={13} /> Eliminar</Button>
                  </>
                }
              >
                <MobileMeta label="Tipo"><Badge variant={p.tipo_servicio === "fiber" ? "default" : "secondary"}>{p.tipo_servicio}</Badge></MobileMeta>
                <MobileMeta label="Velocidad" mono>
                  <span className="inline-flex items-center gap-0.5"><ArrowDown size={10} />{p.velocidad_down_mbps}</span>
                  <span className="mx-1 text-muted-foreground">/</span>
                  <span className="inline-flex items-center gap-0.5"><ArrowUp size={10} />{p.velocidad_up_mbps}</span> Mbps
                </MobileMeta>
                <MobileMeta label="Precio mensual" mono>{formatMXN(p.precio)}</MobileMeta>
                <MobileMeta label="Clientes"><span className="inline-flex items-center gap-1"><Users size={11} className="text-muted-foreground" />{p.contratos_count}</span></MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Velocidades</TableHead>
              <TableHead>Precio mensual</TableHead>
              <TableHead>Clientes</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead className="text-right pr-4">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Package}
                  title="Sin planes"
                  description={search || tipo ? "No hay coincidencias." : "Crea el primer plan de velocidad."}
                  action={!search && !tipo ? (
                    <Button size="sm" onClick={() => setCreateOpen(true)}>
                      <Plus size={14} /> Nuevo plan
                    </Button>
                  ) : undefined}
                />
              </TableEmpty>
            ) : (
              items.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => setEditingId(p.id)}>
                  <TableCell className="font-medium">
                    {p.nombre}
                    {p.descripcion && <div className="text-[10px] text-muted-foreground">{p.descripcion}</div>}
                  </TableCell>
                  <TableCell><Badge variant={p.tipo_servicio === "fiber" ? "default" : "secondary"}>{p.tipo_servicio}</Badge></TableCell>
                  <TableCell className="font-mono">
                    <span className="inline-flex items-center gap-0.5"><ArrowDown size={10} />{p.velocidad_down_mbps}</span>
                    <span className="mx-1 text-muted-foreground">/</span>
                    <span className="inline-flex items-center gap-0.5"><ArrowUp size={10} />{p.velocidad_up_mbps}</span>
                    <span className="ml-1 text-muted-foreground">Mbps</span>
                  </TableCell>
                  <TableCell>{formatMXN(p.precio)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 text-xs">
                      <Users size={11} className="text-muted-foreground" />
                      <span className={p.contratos_count > 0 ? "font-medium" : "text-muted-foreground"}>
                        {p.contratos_count}
                      </span>
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={p.activo ? "success" : "secondary"}>{p.activo ? "activo" : "inactivo"}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => setEditingId(p.id)} title="Editar">
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(p)}
                        disabled={p.contratos_count > 0}
                        title={
                          p.contratos_count > 0
                            ? `No se puede eliminar — tiene ${p.contratos_count} contrato(s) activos`
                            : "Eliminar"
                        }
                      >
                        <Trash2
                          size={14}
                          className={p.contratos_count > 0 ? "text-muted-foreground" : "text-destructive"}
                        />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}{isFetching && " · actualizando…"}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </div>
        )}
      </div>
      )}

      <PlanFormDialog open={createOpen} onOpenChange={setCreateOpen} plan={null} />
      <PlanFormDialog open={!!editingId && !!editing} onOpenChange={(v) => { if (!v) setEditingId(null); }} plan={editing ?? null} />
    </div>
  );
}
